import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "../Components/Seo.jsx";
import PageHeader from "../Components/PageHeader.jsx";
import { trackEvent } from "../utils/analytics.js";
import { ORG } from "../seo/site.js";

// Where Stripe returns the donor after Checkout. The payment is confirmed
// with Stripe through donate-checkout.php before anything is shown or
// counted, so opening this URL directly never claims a donation was made.

const PATH = "/donate/thank-you/";

const DonateThanks = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || "";
  // "checking" | "paid" | "unpaid" | "unknown"
  const [state, setState] = useState(sessionId ? "checking" : "unknown");
  const [donation, setDonation] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    fetch(`/donate-checkout.php?session_id=${encodeURIComponent(sessionId)}`, {
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(15000),
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error("unavailable"))))
      .then((data) => {
        if (cancelled) return;
        setDonation(data);
        setState(data.paid ? "paid" : "unpaid");
        // Count each confirmed donation once per browser session; no personal data.
        const key = `donation-tracked-${sessionId}`;
        try {
          if (data.paid && data.livemode && !sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            trackEvent("donation", { currency: "AUD", value: data.amount, frequency: data.frequency });
          }
        } catch {
          // sessionStorage blocked (private mode): skip analytics, nothing else changes.
        }
      })
      .catch(() => !cancelled && setState("unknown"));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <>
      <Seo path={PATH} />
      <PageHeader
        title={state === "paid" ? "Thank You for Your Donation" : "Donation"}
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house" },
          { label: "Donate", href: "/donate/" },
          { label: "Thank you", active: true },
        ]}
      />

      <section className="py-5">
        <div className="container py-lg-4">
          <div className="col-lg-8 mx-auto text-center" role="status" aria-live="polite">
            {state === "checking" && (
              <>
                <div className="spinner-border text-secondary mb-3" aria-hidden="true"></div>
                <p className="text-muted">Confirming your donation…</p>
              </>
            )}

            {state === "paid" && (
              <>
                <i className="bi bi-heart-fill red-text display-4 d-block mb-3" aria-hidden="true"></i>
                <p className="fs-4 fw-bold mb-2">
                  Thank you for your {donation?.frequency === "monthly" ? "monthly donation" : "donation"}
                  {donation?.amount ? ` of $${Number(donation.amount).toLocaleString("en-AU")}` : ""}.
                </p>
                <p className="text-muted">
                  Your support helps {ORG.name} teach Tamil language and culture
                  to children in Western Sydney. Stripe has emailed you a
                  receipt for this payment.
                </p>
                {donation?.frequency === "monthly" && (
                  <p className="text-muted">
                    The same amount will be charged each month. To change or
                    cancel it at any time, contact us on{" "}
                    <a href={`tel:${ORG.phones.committee.tel}`} className="red-text fw-semibold">
                      {ORG.phones.committee.display}
                    </a>{" "}
                    or{" "}
                    <a href={`mailto:${ORG.email}`} className="red-text fw-semibold">{ORG.email}</a>.
                  </p>
                )}
                {donation?.livemode === false && (
                  <p className="small text-muted">(Test payment: no real money was taken.)</p>
                )}
              </>
            )}

            {state === "unpaid" && (
              <>
                <p className="fs-4 fw-bold mb-2">Your donation was not completed</p>
                <p className="text-muted">You have not been charged. You are welcome to try again.</p>
              </>
            )}

            {state === "unknown" && (
              <>
                <p className="fs-4 fw-bold mb-2">We couldn't confirm a donation</p>
                <p className="text-muted">
                  If you completed a payment, Stripe will have emailed you a
                  receipt. If you have any questions, contact us on{" "}
                  <a href={`tel:${ORG.phones.committee.tel}`} className="red-text fw-semibold">
                    {ORG.phones.committee.display}
                  </a>{" "}
                  or{" "}
                  <a href={`mailto:${ORG.email}`} className="red-text fw-semibold">{ORG.email}</a>.
                </p>
              </>
            )}

            {state !== "checking" && (
              <div className="d-flex flex-wrap justify-content-center gap-3 mt-4">
                {state !== "paid" && (
                  <Link to="/donate/" className="btn red-bg text-white rounded-5 px-4 py-2 fw-semibold border-bottom-left">
                    Back to donations
                  </Link>
                )}
                <Link to="/" className="btn blue-bg text-white rounded-5 px-4 py-2 fw-semibold border-bottom-left">
                  Return to home
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default DonateThanks;
